import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
  faBirthdayCake,
  faClose,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { GridColDef, GridRowClassNameParams, GridValidRowModel } from "@mui/x-data-grid";
import { isAfter } from "date-fns";
import React, { useCallback, useContext, useRef, useState } from "react";
import { DefaultExtensionType, FileIcon } from "react-file-icon";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DIGEST_NOTIFICATION_TYPES } from "src/utils/constants/constants";
import { v4 as uuidv4 } from "uuid";

import { AddEditTimesheetConfirmationRequest } from "./AddEditTimesheetConfiirmationRequest/AddEditTimesheetConfirmationRequest";
import ConfirmationDialog, { DialogOptions } from "./ConfirmationDialog/ConfirmationDialog";
import ExportTimesheetConfirmationsToExcel from "./ExportTimesheetConfirmationsToExcel/ExportTimesheetConfirmationsToExcel";
import { TableContainer } from "./styles";

import { EmployeeNameContainer, SignOffBlock, SignOffDate, StyledFontAwesomeIcon } from "./styles";
import TnAActions from "./TnAActions/TnAActions";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { useAppSelector } from "../../../custom-hooks/redux/redux";
import useAuthorization, { isAuthorizedEeGroup } from "../../../custom-hooks/useAuthorization/useAuthorization";
import useForceUpdate from "../../../custom-hooks/useForceUpdate/useForceUpdate";
import { PageByEmployee } from "../../../models/api/page/page-by-employee";
import { RefModel } from "../../../models/base/ref-model";
import { CustomDate } from "../../../models/custom-date/custom-date";
import { EmployeeRef } from "../../../models/employees/employee-ref";
import {
  TnATimesheetConfirmationsDTO,
  TnATimesheetConfirmationsFilter,
  TnATimesheetConfirmationSTO,
} from "../../../models/tna-timesheet-confirmation";
import { TnATimesheetSignOffRef } from "../../../models/tna-timesheet-confirmation/tna-timesheet-sign-off-ref";
import { StyledTextButton } from "../../../reusable-components/AddEditButton/styles";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { customFileIconStyles } from "../../../reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import DocumentViewer from "../../../reusable-components/DocumentViewer/DocumentViewer";
import { READABLE_FILE_TYPES, READABLE_IMAGE_TYPES } from "../../../reusable-components/DocumentViewer/fileTypes";
import { Actions, ColumnsHeaderTitle } from "../../../reusable-components/Table/styles";
import Table from "../../../reusable-components/Table/Table";
import { handleDownloadFile } from "../../../services/documents/document-files-api.service";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";
import { TnATimesheetConfirmationsApiService } from "../../../services/tna-timesheet-confirmations/tna-timesheet-confirmations-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_MTPAL } from "../../../utils/constants/filter-tables-constants";
import { getFileExtension } from "../../../utils/files-utils/get-file-extension";
import { intlFormatDateShort, intlFormatDateWithHour } from "../../../utils/time-utils";
import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";
import { openTimesheetViewMode } from "../../../views/my-t-and-a/my-ee-tna-utils";
import { DocumentViewState } from "../../Document/DocumentHeader/interfaces";
import { fileSize } from "../../Document/NewFile/utils";
import EmailInfoPopover from "../../EmailInfoPopover/EmailInfoPopover";
import { fromEmailParamsName } from "../../NavigateToLoginPage/NavigateToLoginPage";
import QuickfiltersContainer from "../../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { MyTnAContext } from "../MyTeamPresenceAndLeaves";
import SendMassNotificationsWrapper from "../TnAMassNotifications/SendMassNotificationsWrapper";

export interface State {
  pagingByEe: TnATimesheetConfirmationsFilter;
  loading: boolean;
  error: string;
}

const SIGN_OFFS_PAGE_SIZE = 5;

const TnATimeSheetConfirmation = () => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_time_sheet_confirmation";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";
  const [searchParams] = useSearchParams();
  const fromEmail = searchParams.get(fromEmailParamsName);
  const highlightedValue = searchParams.get("hl");
  const theme = useTheme();
  const { currentUserAuthorizations, currentUserNotificationPreferences } = useAppSelector(
    (state) => state.currentUserReducer
  );
  const [documentViewState, setDocumentViewState] = useState<DocumentViewState>({
    isViewing: false,
    fileDto: null,
  });

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
    searchingDate: { year, month, yearMonthQuickFilter },
  } = useContext(MyTnAContext);

  const allItems = useRef<TnATimesheetConfirmationsDTO[]>([]);

  const [state, setState] = useState<State>({
    pagingByEe: {
      pagingByEmployee: [] as PageByEmployee[],
    } as TnATimesheetConfirmationsFilter,
    loading: false,
    error: "",
  });

  const navigate = useNavigate();
  const { forceUpdate } = useForceUpdate();

  const clickNavigate = (employee: EmployeeRef | null) => {
    if (!hasAccess && allowedToViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (hasAccess) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
    }
  };

  const isFirstSignOffsPage = (employeeId: string | undefined): boolean => {
    const newPagingByEe = state.pagingByEe;
    let eePaging = newPagingByEe.pagingByEmployee.find((item) => item.employeeId === employeeId);

    if (!eePaging) {
      eePaging = {
        employeeId,
        pageNumber: 0,
        pageSize: SIGN_OFFS_PAGE_SIZE,
      } as PageByEmployee;
      newPagingByEe.pagingByEmployee.push(eePaging);
    }

    return eePaging.pageNumber === 0;
  };

  const isLastSignOffsPage = (employeeId: string | undefined, totalItems: number): boolean => {
    const newPagingByEe = state.pagingByEe;
    let eePaging = newPagingByEe.pagingByEmployee.find((item) => item.employeeId === employeeId);

    if (!eePaging) {
      eePaging = {
        employeeId,
        pageNumber: 0,
        pageSize: SIGN_OFFS_PAGE_SIZE,
      } as PageByEmployee;
      newPagingByEe.pagingByEmployee.push(eePaging);
    }

    const maxPage = Math.trunc(totalItems / eePaging.pageSize) + (totalItems % eePaging.pageSize > 0 ? 1 : 0);

    return eePaging.pageNumber === maxPage - 1;
  };

  const setFirstSignOffsPage = async (employeeId: string | undefined): Promise<void> => {
    const newPagingByEe = state.pagingByEe;
    let eePaging = newPagingByEe.pagingByEmployee.find((item) => item.employeeId === employeeId);

    if (!eePaging) {
      eePaging = {
        employeeId,
        pageNumber: 0,
        pageSize: SIGN_OFFS_PAGE_SIZE,
      } as PageByEmployee;
      newPagingByEe.pagingByEmployee.push(eePaging);
    }

    eePaging.pageNumber = 0;

    setState((state) => ({
      ...state,
      pagingByEe: {
        ...newPagingByEe,
      },
    }));
    if (forceUpdate) {
      forceUpdate();
    }
  };

  const setLastSignOffsPage = async (employeeId: string | undefined, totalItems: number): Promise<void> => {
    const newPagingByEe = state.pagingByEe;
    let eePaging = newPagingByEe.pagingByEmployee.find((item) => item.employeeId === employeeId);

    if (!eePaging) {
      eePaging = {
        employeeId,
        pageNumber: 0, // as they ask next page
        pageSize: SIGN_OFFS_PAGE_SIZE,
      } as PageByEmployee;
      newPagingByEe.pagingByEmployee.push(eePaging);
    }

    const maxPage = Math.trunc(totalItems / eePaging.pageSize) + (totalItems % eePaging.pageSize > 0 ? 1 : 0);
    eePaging.pageNumber = maxPage - 1;

    setState((state) => ({
      ...state,
      pagingByEe: {
        ...newPagingByEe,
      },
    }));
    if (forceUpdate) {
      forceUpdate();
    }
  };

  const handleChangeNextPrevSignOffsPage = async (
    employeeId: string | undefined,
    totalItems: number,
    prevPage = false
  ): Promise<void> => {
    const newPagingByEe = state.pagingByEe;
    let eePaging = newPagingByEe.pagingByEmployee.find((item) => item.employeeId === employeeId);

    if (!eePaging) {
      eePaging = {
        employeeId,
        pageNumber: prevPage ? 0 : 1,
        pageSize: SIGN_OFFS_PAGE_SIZE,
      } as PageByEmployee;
      newPagingByEe.pagingByEmployee.push(eePaging);
    } else {
      const maxPage = Math.trunc(totalItems / eePaging.pageSize) + (totalItems % eePaging.pageSize > 0 ? 1 : 0);
      eePaging.pageNumber = prevPage
        ? eePaging.pageNumber > 0
          ? --eePaging.pageNumber
          : 0
        : eePaging.pageNumber < maxPage - 1
        ? ++eePaging.pageNumber
        : eePaging.pageNumber;
    }

    setState((state) => ({
      ...state,
      pagingByEe: {
        ...newPagingByEe,
      },
    }));
    if (forceUpdate) {
      forceUpdate();
    }
  };

  const remindRequest = useCallback(
    async (item: TnATimesheetConfirmationSTO) => {
      try {
        const apiService = new TnATimesheetConfirmationsApiService();
        apiService.useSaveItemsPath("/SendSignOffReminderNotification");
        const response = await apiService.saveItems([
          {
            id: uuidv4(),
            isDeleted: false,
            employee: item.employee,
          } as TnATimesheetConfirmationSTO,
        ]);

        if (!response.error) {
          forceUpdate();
        } else {
          console.log("error: ", response.error);
        }

        toastifyResponse(response);
      } catch (error) {
        console.log(error);
      }
    },
    [forceUpdate]
  );

  const cancelRequest = useCallback(
    async (item: TnATimesheetConfirmationSTO) => {
      try {
        const service = new TnATimesheetConfirmationsApiService();
        service.useSaveItemsPath("/SaveCancellationTnATimesheetConfirmations");

        const response = await service.saveItems([
          {
            ...item,
            isCanceled: true,
          } as TnATimesheetConfirmationSTO,
        ]);

        if (!response.error) {
          forceUpdate();
        } else {
          console.log("error: ", response.error);
        }

        toastifyResponse(response);
      } catch (error) {
        console.log(error);
      }
    },
    [forceUpdate]
  );

  const columns: GridColDef<TnATimesheetConfirmationsDTO>[] = [
    {
      field: "employee.employeeCode, employee.internalId",
      headerName: `${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    clickNavigate(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      renderHeader: () => (
        <Grid>
          <Typography>{`${t("common_words.employee")} | ${t("common_words.legal_entity")}`}</Typography>
          <EmailInfoPopover
            autoOpen={!!fromEmail}
            message={"Only values of employees for whom you are an approver are highlighted"}
          />
        </Grid>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "employee.tnAGroup",
      renderHeader: () => (
        <Tooltip title={`${t("common_words.tna_employee_group")}`}>
          <ColumnsHeaderTitle>{`${t("common_words.tna_employee_group")}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "asOfDate",
      renderCell: ({ row }) => (
        <span
          title={
            row.asOfDate
              ? `Sign-off ${
                  row.fromDate ? `${intlFormatDateShort(CustomDate.ToDate(row.fromDate) ?? new Date())}-` : ""
                }
              ${intlFormatDateShort(CustomDate.ToDate(row.asOfDate) ?? new Date())}\r\nrequested by
              ${row.createdByUser?.name ?? row.createdByUser?.email} at ${intlFormatDateWithHour(row.createdAt)} `
              : ""
          }
          style={{
            color: !row.isSignedOff ? theme.palette.error.main : "",
          }}
        >
          {row.asOfDate ? intlFormatDateShort(CustomDate.ToDate(row.asOfDate) ?? new Date()) : ""}
        </span>
      ),
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.asOf_date`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.asOf_date`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 2,
      minWidth: 100,
      maxWidth: 130,
      align: "center",
    },
    {
      field: "signedOffAt",
      renderCell: ({ row }) => (
        <span
          title={`${t(`${translationFields}.signedOffAt`)} ${
            row.signedOffAt ? intlFormatDateWithHour(row.signedOffAt) : ""
          } by ${row.signedOffBy?.name ?? row.signedOffBy?.email}`}
        >
          {row.signedAsOfDate ? intlFormatDateShort(CustomDate.ToDate(row.signedAsOfDate) ?? new Date()) : ""}
        </span>
      ),
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.signedAsOf_date`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.signedAsOf_date`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      maxWidth: 130,
      align: "center",
    },
    {
      field: "signOffs",
      renderCell: ({ row }) => (
        <Grid
          key={0}
          style={{ display: "flex", gap: "10px" }}
        >
          {!isFirstSignOffsPage(row.employee?.id) && (
            <React.Fragment>
              <FontAwesomeIcon
                icon={faAngleDoubleLeft}
                title="To first page"
                style={{
                  color: "darkblue",
                  fontSize: "1rem",
                  cursor: "pointer",
                  marginRight: "3px",
                }}
                size="2x"
                onClick={() => setFirstSignOffsPage(row.employee?.id)}
              />
              <FontAwesomeIcon
                icon={faAngleLeft}
                title="To next page"
                style={{
                  color: "darkblue",
                  fontSize: "1rem",
                  cursor: "pointer",
                }}
                size="2x"
                onClick={() => handleChangeNextPrevSignOffsPage(row.employee?.id, row.totalSignOffs, true)}
              />
            </React.Fragment>
          )}
          {row.signOffs?.map((signOff: TnATimesheetSignOffRef, idx: number) => (
            <React.Fragment key={idx}>
              {!!signOff.signedTimesheet &&
              !!signOff.signedTimesheet?.files &&
              !!signOff.signedTimesheet?.files?.length &&
              !!signOff.signedTimesheet?.files[0] ? (
                <React.Fragment>
                  <SignOffBlock>
                    {signOff.signedTimesheet?.files.slice(0, 1).map((file) => {
                      const fileExtension = getFileExtension(file.name).toLowerCase();
                      return (
                        <React.Fragment key={file.id}>
                          {!READABLE_FILE_TYPES.includes(fileExtension) &&
                            !READABLE_IMAGE_TYPES.includes(fileExtension) && (
                              <IconButton
                                title={`"${file.name}" ${fileSize(file.size)} } ${intlFormatDateWithHour(
                                  file.createdAt
                                )}`}
                                onClick={() => handleDownloadFile(file)}
                                sx={{
                                  width: "30px",
                                  margin: "0px",
                                  padding: "0px",
                                }}
                              >
                                <FileIcon
                                  extension={fileExtension}
                                  {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                                  radius={3}
                                />
                              </IconButton>
                            )}

                          {(READABLE_FILE_TYPES.includes(fileExtension) ||
                            READABLE_IMAGE_TYPES.includes(fileExtension)) && (
                            <IconButton
                              onClick={() => {
                                setDocumentViewState((documentViewState) => ({
                                  ...documentViewState,
                                  isViewing: true,
                                  fileDto: file,
                                }));
                              }}
                              sx={{
                                width: "30px",
                                margin: "0px",
                                padding: "0px",
                              }}
                            >
                              <FileIcon
                                extension={fileExtension}
                                {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                                radius={3}
                              />
                            </IconButton>
                          )}
                        </React.Fragment>
                      );
                    })}

                    <SignOffDate> {intlFormatDateShort(CustomDate.ToDate(signOff.asOfDate) ?? new Date())}</SignOffDate>
                  </SignOffBlock>
                </React.Fragment>
              ) : (
                <SignOffBlock>
                  <SignOffDate> {intlFormatDateShort(CustomDate.ToDate(signOff.asOfDate) ?? new Date())}</SignOffDate>
                </SignOffBlock>
              )}
            </React.Fragment>
          ))}
          {!isLastSignOffsPage(row.employee?.id, row.totalSignOffs) && !!row.totalSignOffs && (
            <React.Fragment>
              <FontAwesomeIcon
                icon={faAngleRight}
                title="To previous page"
                style={{
                  color: "darkblue",
                  fontSize: "1rem",
                  cursor: "pointer",
                  marginRight: "3px",
                }}
                size="2x"
                onClick={() => handleChangeNextPrevSignOffsPage(row.employee?.id, row.totalSignOffs)}
              />
              <FontAwesomeIcon
                icon={faAngleDoubleRight}
                title="To last page"
                style={{
                  color: "darkblue",
                  fontSize: "1rem",
                  cursor: "pointer",
                }}
                size="2x"
                onClick={() => setLastSignOffsPage(row.employee?.id, row.totalSignOffs)}
              />
            </React.Fragment>
          )}
        </Grid>
      ),

      headerName: t(`${translationFields}.sign_offs`),
      headerAlign: "center",
      flex: 3,
      minWidth: 140,
      align: "left",
    },

    {
      field: "",
      renderCell: ({ row }) => (
        <Actions>
          <TnAActions
            RequestElement={
              <AddEditTimesheetConfirmationRequest
                effect="edit"
                defaultLegalEntity={row.legalEntity}
                item={row}
                handleNotifyParent={forceUpdate}
                readOnly={!hasAccess && allowedToViewOnly}
              >
                <StyledTextButton disabled={!hasAccess && allowedToViewOnly}>{`${t(
                  `${tRP}.request_signOff`
                )}`}</StyledTextButton>
              </AddEditTimesheetConfirmationRequest>
            }
            RemindElement={
              <ConfirmationDialog
                item={row}
                sendRequest={remindRequest}
                disabled={!hasAccess && allowedToViewOnly ? true : !(!row.isSignedOff && row.asOfDate)}
                option={DialogOptions.remind}
              >
                <StyledTextButton
                  disabled={!hasAccess && allowedToViewOnly ? true : !(!row.isSignedOff && row.asOfDate)}
                >
                  {`${t(`${tRP}.send_signoff_reminder`)}`}
                </StyledTextButton>
              </ConfirmationDialog>
            }
            CancelRequestElement={
              <ConfirmationDialog
                item={row}
                sendRequest={cancelRequest}
                disabled={!hasAccess && allowedToViewOnly ? true : !(!row.isSignedOff && row.asOfDate)}
                option={DialogOptions.cancel}
              >
                <StyledTextButton
                  disabled={!hasAccess && allowedToViewOnly ? true : !(!row.isSignedOff && row.asOfDate)}
                >
                  {`${t(`${tRP}.cancel_signoff_request`)}`}
                </StyledTextButton>
              </ConfirmationDialog>
            }
          />
        </Actions>
      ),
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.request_signOff`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.request_signOff`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      maxWidth: 100,
      align: "center",
    },
  ];

  const defaultItem: Partial<TnATimesheetConfirmationSTO> = {
    legalEntity: selectedLegalEntity ?? null,
    employee: null,
    asOfDate: CustomDate.FromDate(new Date()),
    signedAsOfDate: null,
  };

  const loadEmployeeGroup = useCallback(async () => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [employeeGroupOption?.employeeGroupIds]);

  const loadTnATimesheetConfirmationData = useCallback(
    async (pageSize = 10) => {
      try {
        const response = await new TnATimesheetConfirmationsApiService().loadItems(
          {
            ...state.pagingByEe,
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
            onlyCurrentEmployees: !!onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
            year: 0, // no need for year filter here
            month: month + 1,
          } as TnATimesheetConfirmationsFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      legalEntityOption?.legalEntityIds,
      month,
      state.pagingByEe,
    ]
  );

  const quickfilters = useCallback(
    (items: RefModel[], loading: boolean) => {
      return (
        <QuickfiltersContainer>
          <>
            {!!legalEntityQuickFilter && legalEntityQuickFilter}
            {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
            {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
            <ExportTimesheetConfirmationsToExcel
              loadEmployeeGroup={loadEmployeeGroup}
              loadTnATimesheetConfirmationData={loadTnATimesheetConfirmationData}
              disabled={state.loading || loading || !items?.length}
              allItems={() => allItems.current}
            />
            <SendMassNotificationsWrapper
              handleNotifyParent={forceUpdate}
              buttonVariant="contained"
              customButtonStyle={{
                textWrap: "nowrap",
                background:
                  state.loading || loading || !items?.length ? theme.palette.grey[300] : theme.palette.primary.main,
                textTransform: "uppercase",
                color:
                  state.loading || loading || !items?.length
                    ? theme.palette.grey[500]
                    : theme.palette.primary.contrastText,
              }}
              buttonTitle={t(`${tRP}.send_mass_notifications`)}
              buttonText={t(`${tRP}.send_mass_notifications`)}
              disabled={state.loading || loading || !items?.length}
            />
          </>
        </QuickfiltersContainer>
      );
    },
    [
      legalEntityQuickFilter,
      employeeGroupQuickFilter,
      onlyCurrentEmployeesQuickFilter,
      loadEmployeeGroup,
      loadTnATimesheetConfirmationData,
      state.loading,
      forceUpdate,
      theme.palette.grey,
      theme.palette.primary.main,
      theme.palette.primary.contrastText,
      t,
    ]
  );

  const highlightRow = (params: GridRowClassNameParams<GridValidRowModel>): string => {
    const tnaGroupId = params.row.employee?.tnAGroup?.id;
    const isApproverForThisEe =
      !!tnaGroupId &&
      isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves],
        currentUserAuthorizations,
        [tnaGroupId],
        true
      );

    if (!!fromEmail && highlightedValue === "without-signoff-request") {
      if (isApproverForThisEe) {
        const notification = currentUserNotificationPreferences.find(
          (notif) => notif.notificationType?.id === DIGEST_NOTIFICATION_TYPES.my_team_timesheet.id
        );

        const notitifcationSection = notification?.digestSectionsParams.find(
          (section) =>
            section.digestSection?.id ===
            DIGEST_NOTIFICATION_TYPES.my_team_timesheet.sections.SignoffRequestNotSentForPreviousMonthId
        );

        const delayPreference = notitifcationSection?.value;

        if (delayPreference) {
          const trackingStartsThisMonth =
            params.row.timesheetTrackingStartDate &&
            new Date(params.row.timesheetTrackingStartDate).getFullYear() === new Date().getFullYear() &&
            new Date(params.row.timesheetTrackingStartDate).getMonth() === new Date().getMonth();

          const trackingIsInFuture =
            params.row.timesheetTrackingStartDate && isAfter(params.row.timesheetTrackingStartDate, new Date());

          const noLastSignOffForPreviousMonth =
            (new Date().getMonth() - (params.row.signedAsOfDate?.month - 1) === 1 && //last signed off was last month
              new Date().getDate() > 10) ||
            new Date().getMonth() - (params.row.signedAsOfDate?.month - 1) > 1;

          return !trackingStartsThisMonth &&
            !trackingIsInFuture &&
            (noLastSignOffForPreviousMonth || !params.row.signedAsOfDate)
            ? "highlighted"
            : "";
        }
        return "";
      }
    }
    return "";
  };

  return (
    <TableContainer>
      {(hasAccess || allowedToViewOnly) && (
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <Table
            defaultItem={defaultItem}
            translationRootPath={tRP}
            columns={columns}
            serviceApi={new TnATimesheetConfirmationsApiService()}
            propsFilter={
              {
                ...state.pagingByEe,
                ...legalEntityOption,
                ...employeeGroupOption,
                ...onlyCurrentEmployeesOptionValue,
                year: 0, // no need for year filter here
                month: month + 1,
              } as TnATimesheetConfirmationsFilter
            }
            filter={FT_MTPAL}
            CustomAddEditComponent={AddEditTimesheetConfirmationRequest}
            addEditType={"custom"}
            verticalOrientation
            quickFilters={quickfilters}
            readOnly
            getAllItems={(items) => {
              allItems.current = items as TnATimesheetConfirmationsDTO[];
            }}
            getRowClassNameFromSx={highlightRow}
          />
        </LoadingOverlay>
      )}
      {!!documentViewState.fileDto && (
        <Popover
          open={documentViewState.isViewing}
          anchorPosition={{ left: 0, top: 0 }}
          anchorReference={"anchorPosition"}
        >
          <IconButton
            onClick={() => {
              setDocumentViewState({
                ...documentViewState,
                isViewing: false,
                fileDto: null,
              });
            }}
          >
            <StyledFontAwesomeIcon icon={faClose} />
          </IconButton>
          {documentViewState.fileDto && (
            <DocumentViewer
              fileDto={documentViewState.fileDto}
              bypasAllowDownload
            />
          )}
        </Popover>
      )}
    </TableContainer>
  );
};

export default TnATimeSheetConfirmation;
